import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Loader2, ShieldCheck, ClipboardCheck, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const PREFILL_KEY = 'governwell_healthcheck_prefill';

const HealthCheckPrefillBanner = ({ navigate }) => {
  const [prefill, setPrefill] = useState(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(PREFILL_KEY);
      if (raw) setPrefill(JSON.parse(raw));
    } catch (e) {
      setPrefill(null);
    }
  }, []);

  if (!prefill) return null;

  const dismiss = () => {
    localStorage.removeItem(PREFILL_KEY);
    setPrefill(null);
  };

  const continueToReview = () => {
    localStorage.removeItem(PREFILL_KEY);
    navigate('/dashboard/governance-review');
  };

  const org = prefill.contact?.organisation_name;
  const percent = prefill.results?.overall_percent;
  const maturity = prefill.results?.maturity_level;

  return (
    <div className="mb-8 bg-lime border border-ink/10 rounded-lg shadow-saas">
      <div className="p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 bg-ink text-lime border border-ink/10 rounded-lg">
            <ClipboardCheck size={24} />
          </div>
          <div>
            <h3 className="font-bold tracking-tight text-2xl">
              {org ? `Welcome back, ${org}` : 'Your Health Check is saved'}
            </h3>
            <p className="font-medium text-ink/80 mt-1 max-w-xl">
              {typeof percent === 'number'
                ? `You scored ${percent}% (${maturity}) on the Governance Health Check. Continue into a full review to go deeper.`
                : 'Continue into a full governance review to go deeper.'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={continueToReview}
            className="inline-flex items-center px-6 py-3 bg-ink text-white border border-ink/10 rounded-lg font-bold uppercase tracking-wide"
          >
            Continue
            <ArrowRight size={16} className="ml-2" />
          </button>
          <button
            onClick={dismiss}
            className="p-3 border border-ink/10 rounded-lg bg-white"
            aria-label="Dismiss"
          >
            <X size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

const RAG_BG = {
  red: 'bg-red-600 text-white',
  amber: 'bg-amber-500 text-black',
  green: 'bg-green-600 text-white',
  blue: 'bg-blue-600 text-white',
  grey: 'bg-gray-400 text-black',
};

const EntityGovernanceWidget = ({ entity, navigate }) => {
  const review = entity.active_review;
  const summary = review?.summary;
  const overall = summary?.overall;

  return (
    <div className="mb-8 bg-white border border-ink/10 rounded-lg shadow-saas">
      <div className="p-6 border-b border-ink/10 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-lime border border-ink/10 rounded-lg">
            <ShieldCheck size={20} />
          </div>
          <div>
            <h3 className="font-bold tracking-tight text-2xl leading-none">Governance Review</h3>
            <p className="font-bold uppercase text-xs tracking-wide text-gray-600 mt-1">
              {entity.organisation_label}
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate('/dashboard/governance-review')}
          className="inline-flex items-center px-5 py-2.5 bg-ink text-white border border-ink/10 rounded-lg font-bold uppercase tracking-wide text-sm"
        >
          Open Review
          <ArrowRight size={16} className="ml-2" />
        </button>
      </div>

      {!review ? (
        <div className="p-6">
          <p className="font-medium text-gray-700">
            Your organisation type is set. Start your tailored assessment to generate findings,
            recommendations and a downloadable report.
          </p>
        </div>
      ) : (
        <div className="p-6 space-y-6">
          {/* Metric cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="border border-ink/10 rounded-lg p-4">
              <p className="font-bold uppercase text-xs tracking-wide text-gray-500">Assessment</p>
              <p className="font-bold tracking-tight text-3xl mt-1">{review.progress?.percent ?? 0}%</p>
              <p className="font-medium text-xs text-gray-500 mt-1">
                {review.progress?.answered ?? 0}/{review.progress?.total ?? 0} answered
              </p>
            </div>
            <div className="border border-ink/10 rounded-lg p-4">
              <p className="font-bold uppercase text-xs tracking-wide text-gray-500">Documents</p>
              <p className="font-bold tracking-tight text-3xl mt-1">{review.documents_count ?? 0}</p>
              <p className="font-medium text-xs text-gray-500 mt-1">
                of {review.document_types_total ?? 0} types
              </p>
            </div>
            <div className="border border-ink/10 rounded-lg p-4">
              <p className="font-bold uppercase text-xs tracking-wide text-gray-500">Maturity</p>
              <p className="font-bold tracking-tight text-3xl mt-1">
                {overall?.score != null ? overall.score : '—'}
              </p>
              <p className="font-medium text-xs text-gray-500 mt-1">
                {overall?.maturity_level || 'Not generated'}
              </p>
            </div>
            <div className={`border border-ink/10 rounded-lg p-4 ${overall?.rag ? RAG_BG[overall.rag] : ''}`}>
              <p className="font-bold uppercase text-xs tracking-wide opacity-80">RAG Status</p>
              <p className="font-bold tracking-tight text-xl mt-1 leading-tight">
                {overall?.rag_meaning || (overall?.rag ? overall.rag.toUpperCase() : 'Pending')}
              </p>
            </div>
          </div>

          {/* Pillar scores */}
          {summary?.pillar_findings?.length > 0 && (
            <div>
              <p className="font-bold uppercase text-xs tracking-wide text-gray-500 mb-2">
                Pillar Scores
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {summary.pillar_findings.map((p) => (
                  <div
                    key={p.pillar_name}
                    className="flex items-center justify-between border border-ink/10 rounded-lg px-3 py-2"
                  >
                    <span className="font-medium text-sm truncate mr-2">{p.pillar_name}</span>
                    <span
                      className={`font-bold text-xs uppercase px-2 py-1 border border-ink/10 rounded-lg ${RAG_BG[p.rag] || 'bg-gray-200'}`}
                    >
                      {p.score != null ? `${p.score}/5` : '—'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Key risks + priority actions */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <p className="font-bold uppercase text-xs tracking-wide text-gray-500 mb-2">
                Key Risks
              </p>
              {summary?.key_risks?.length > 0 ? (
                <ul className="space-y-2">
                  {summary.key_risks.map((r, i) => (
                    <li key={i} className="border border-ink/10 rounded-lg px-3 py-2">
                      <span className="font-bold text-xs uppercase mr-2">[{r.priority}]</span>
                      <span className="font-medium text-sm">{r.title}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="font-medium text-sm text-gray-500">
                  {summary ? 'No high-priority risks identified.' : 'Generate the review to see risks.'}
                </p>
              )}
            </div>
            <div>
              <p className="font-bold uppercase text-xs tracking-wide text-gray-500 mb-2">
                Priority Actions (90-Day)
              </p>
              {summary?.plan_90day?.length > 0 ? (
                <ul className="space-y-2">
                  {summary.plan_90day.map((a, i) => (
                    <li key={i} className="border border-ink/10 rounded-lg px-3 py-2">
                      <span className="font-bold text-xs uppercase mr-2">[{a.priority}]</span>
                      <span className="font-medium text-sm">{a.action}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="font-medium text-sm text-gray-500">
                  {summary ? 'No priority actions.' : 'Generate the review to see your action plan.'}
                </p>
              )}
            </div>
          </div>

          {/* Reference sources */}
          {entity.reference_sources?.length > 0 && (
            <div>
              <p className="font-bold uppercase text-xs tracking-wide text-gray-500 mb-2">
                Reference Library
              </p>
              <div className="flex flex-wrap gap-2">
                {entity.reference_sources.map((r) => (
                  <span
                    key={r}
                    className="font-medium text-xs border border-ink/10 rounded-lg px-2 py-1 bg-gray-50"
                  >
                    {r}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [entity, setEntity] = useState(null);

  useEffect(() => {
    fetchEntityDashboard();
  }, []);

  const fetchEntityDashboard = async () => {
    try {
      setLoading(true);
      const data = await api.getEntityDashboard();
      setEntity(data);
    } catch (error) {
      setEntity(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="animate-spin" size={32} />
          <p className="font-bold uppercase tracking-wide">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const configured = entity && entity.configured;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-bold tracking-tight text-4xl md:text-5xl mb-3">
          Governance Review System
        </h1>
        <p className="font-medium text-ink/70 max-w-2xl">
          Welcome, {user?.email?.split('@')[0] || 'User'}. Run a single, structured governance
          review — planning, documents, assessment, improvement plan and report — all in one place.
        </p>
      </div>

      <HealthCheckPrefillBanner navigate={navigate} />

      {!configured ? (
        <div className="mb-8 bg-ink text-white border border-ink/10 rounded-lg shadow-saas">
          <div className="p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 bg-lime text-black border border-ink/10 rounded-lg">
                <ShieldCheck size={24} />
              </div>
              <div>
                <h3 className="font-bold tracking-tight text-2xl">Start Your Governance Review</h3>
                <p className="font-medium text-white/80 mt-1 max-w-xl">
                  Pick your organisation type (charity, company, startup or PLC) for a tailored
                  governance review and downloadable report.
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/dashboard/governance-review')}
              className="inline-flex items-center px-6 py-3 bg-lime text-black border border-ink/10 rounded-lg font-bold uppercase tracking-wide flex-shrink-0"
            >
              Get Started
              <ArrowRight size={16} className="ml-2" />
            </button>
          </div>
        </div>
      ) : (
        <EntityGovernanceWidget entity={entity} navigate={navigate} />
      )}
    </div>
  );
};

export default Dashboard;
